var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3;
_react_refresh_temp_3 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Switch, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { updateDisplaySpeechSettingsModal } from '../../../store/slices/bottomIconsActivitySlice';
import { useAppDispatch, useAppSelector } from '../../../store';
import { SpeechToTextTranslationReq } from '../../../helpers/proto/plugnmeet_speech_services_pb';
import { enableOrDisableSpeechService } from '../helpers/apiConnections';
import SpeechLangsElms from './speechLangsElms';
import SpeechUsersElms from './speechUsersElms';
import TransLangsElm from './transLangsElm';
import DefaultSubtitleLangElms from './defaultSubtitleLangElms';
const speechServiceFeaturesSelector = createSelector(_react_refresh_temp_1 = (state) => { var _a; return (_a = state.session.currentRoom.metadata) === null || _a === void 0 ? void 0 : _a.room_features; }, (room_features) => room_features === null || room_features === void 0 ? void 0 : room_features.speech_to_text_translation_features);
$RefreshReg$(_react_refresh_temp_1, "speechServiceFeaturesSelector$createSelector");
const SpeechServiceSettingsModal = () => {
    _react_refresh_temp_3();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const speechService = useAppSelector(speechServiceFeaturesSelector);
    const [selectedSpeechLangs, setSelectedSpeechLangs] = useState([]);
    const [selectedSpeechUsers, setSelectedSpeechUsers] = useState([]);
    const [enableTranslation, setEnableTranslation] = useState(false);
    const [selectedTransLangs, setSelectedTransLangs] = useState([]);
    const [selectedDefaultSubtitleLang, setSelectedDefaultSubtitleLang] = useState('');
    useEffect(() => {
        var _a;
        if (speechService === null || speechService === void 0 ? void 0 : speechService.allowed_speech_langs) {
            setSelectedSpeechLangs(speechService.allowed_speech_langs);
        }
        if (speechService === null || speechService === void 0 ? void 0 : speechService.allowed_speech_users) {
            setSelectedSpeechUsers(speechService.allowed_speech_users);
        }
        if (speechService === null || speechService === void 0 ? void 0 : speechService.allowed_trans_langs) {
            setSelectedTransLangs(speechService.allowed_trans_langs);
        }
        if (speechService === null || speechService === void 0 ? void 0 : speechService.default_subtitle_lang) {
            setSelectedDefaultSubtitleLang(speechService.default_subtitle_lang);
        }
        setEnableTranslation((_a = speechService === null || speechService === void 0 ? void 0 : speechService.is_enabled_translation) !== null && _a !== void 0 ? _a : false);
    }, [speechService]);
    const enableOrUpdateService = async () => {
        if (!selectedSpeechUsers.length) {
            toast(t('speech-services.speech-user-required'), {
                type: 'error',
            });
            return;
        }
        if (!selectedSpeechLangs.length) {
            toast(t('speech-services.speech-lang-required'), {
                type: 'error',
            });
            return;
        }
        if (!enableTranslation && selectedSpeechLangs.length > 1) {
            toast(t('speech-services.enable-translation-warning'), {
                type: 'warning',
            });
            return;
        }
        if (enableTranslation &&
            selectedSpeechLangs.length === 1 &&
            !selectedTransLangs.length) {
            toast(t('speech-services.translation-lang-required'), {
                type: 'error',
            });
            return;
        }
        const body = new SpeechToTextTranslationReq({
            isEnabled: true,
            allowedSpeechLangs: selectedSpeechLangs,
            allowedSpeechUsers: selectedSpeechUsers,
            isEnabledTranslation: enableTranslation,
            allowedTransLangs: selectedTransLangs,
            defaultSubtitleLang: selectedDefaultSubtitleLang,
        });
        const res = await enableOrDisableSpeechService(body);
        if (res.status) {
            toast(t('speech-services.service-ready'), {
                type: 'info',
            });
        }
        else {
            toast(t(res.msg), {
                type: 'error',
            });
            return;
        }
        closeModal();
    };
    const stopService = async () => {
        const body = new SpeechToTextTranslationReq({
            isEnabled: false,
            allowedSpeechLangs: [],
            allowedSpeechUsers: [],
            isEnabledTranslation: false,
            allowedTransLangs: [],
        });
        const res = await enableOrDisableSpeechService(body);
        if (res.status) {
            toast(t('speech-services.service-stopped'), {
                type: 'info',
            });
        }
        else {
            toast(t(res.msg), {
                type: 'error',
            });
            return;
        }
        closeModal();
    };
    const closeModal = () => {
        dispatch(updateDisplaySpeechSettingsModal(false));
    };
    return (_jsx(_Fragment, { children: _jsx(Transition, { appear: true, show: true, as: Fragment, children: _jsx(Dialog, { as: "div", className: "fixed inset-0 z-[9999] overflow-y-auto", onClose: () => false, children: _jsxs("div", { className: "min-h-screen px-4 text-center", children: [_jsx(Transition.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0", children: _jsx(Dialog.Overlay, { className: "fixed inset-0 bg-black opacity-30" }) }), _jsx("span", { className: "inline-block h-screen align-middle", "aria-hidden": "true", children: "\u200B" }), _jsx(Transition.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0 scale-95", enterTo: "opacity-100 scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 scale-100", leaveTo: "opacity-0 scale-95", children: _jsxs("div", { className: "inline-block w-full max-w-lg p-6 my-8 overflow-[inherit] text-left align-middle transition-all transform bg-white dark:bg-darkPrimary shadow-xl rounded-2xl", children: [_jsxs("button", { className: "close-btn absolute top-8 ltr:right-6 rtl:left-6 w-[25px] h-[25px] outline-none", type: "button", onClick: () => closeModal(), children: [_jsx("span", { className: "inline-block h-[1px] w-[20px] bg-primaryColor dark:bg-darkText absolute top-0 left-0 rotate-45" }), _jsx("span", { className: "inline-block h-[1px] w-[20px] bg-primaryColor dark:bg-darkText absolute top-0 left-0 -rotate-45" })] }), _jsx(Dialog.Title, { as: "h3", className: "text-lg font-medium leading-6 text-gray-900 dark:text-white mb-2 ltr:text-left rtl:text-right", children: t('speech-services.modal-settings-title') }), _jsx("hr", {}), _jsxs("div", { className: "mt-6", children: [_jsx(SpeechLangsElms, { selectedSpeechLangs: selectedSpeechLangs, setSelectedMicDevice: setSelectedSpeechLangs }), _jsx(SpeechUsersElms, { selectedSpeechUsers: selectedSpeechUsers, setSelectedSpeechUsers: setSelectedSpeechUsers }), _jsx(Switch.Group, { children: _jsxs("div", { className: "flex items-center justify-between my-4", children: [_jsx(Switch.Label, { className: "ltr:pr-4 rtl:pl-4 w-full dark:text-darkText text-sm", children: t('speech-services.enable-translation') }), _jsx(Switch, { checked: enableTranslation, onChange: setEnableTranslation, className: `${enableTranslation
                                                                ? 'bg-primaryColor dark:bg-darkSecondary2'
                                                                : 'bg-gray-200 dark:bg-secondaryColor'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2`, children: _jsx("span", { className: `${enableTranslation
                                                                    ? 'ltr:translate-x-6 rtl:-translate-x-6'
                                                                    : 'ltr:translate-x-1 rtl:translate-x-0'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform` }) })] }) }), enableTranslation ? (_jsx(TransLangsElm, { selectedTransLangs: selectedTransLangs, setSelectedTransLangs: setSelectedTransLangs })) : null, _jsx(DefaultSubtitleLangElms, { selectedSpeechLangs: selectedSpeechLangs, selectedTransLangs: selectedTransLangs, selectedDefaultSubtitleLang: selectedDefaultSubtitleLang, setSelectedDefaultSubtitleLang: setSelectedDefaultSubtitleLang })] }), _jsxs("div", { className: "py-3 bg-gray-50 dark:bg-transparent text-right mt-4", children: [!(speechService === null || speechService === void 0 ? void 0 : speechService.is_enabled) ? (_jsx("button", { className: "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primaryColor hover:bg-secondaryColor focus:outline-none", onClick: () => enableOrUpdateService(), children: t('speech-services.enable-service') })) : null, (speechService === null || speechService === void 0 ? void 0 : speechService.is_enabled) ? (_jsxs(_Fragment, { children: [_jsx("button", { className: "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primaryColor hover:bg-secondaryColor focus:outline-none ltr:mr-2 rtl:ml-2", onClick: () => enableOrUpdateService(), children: t('speech-services.update-service') }), _jsx("button", { className: "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-secondaryColor hover:bg-secondaryColor focus:outline-none", onClick: () => stopService(), children: t('speech-services.stop-service') })] })) : null] })] }) })] }) }) }) }));
};
_react_refresh_temp_2 = SpeechServiceSettingsModal;
$RefreshReg$(_react_refresh_temp_2, "SpeechServiceSettingsModal");
_react_refresh_temp_3(SpeechServiceSettingsModal, "5RJOFJr6YrrLn4u+nbX1H8R5tls=", false, () => [useTranslation, useAppDispatch, useAppSelector]);
export default SpeechServiceSettingsModal;
